import "./PreUseHistory.scss";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { connect } from "react-redux";
import { getPreUseModuleHeader, getPreUseTimeHeader } from "../../common/constant/common-constants";
import { getVentilatorPreUseHistory } from "../../helper/alarm-helper";
import { convertUTCtoIST } from "../../common/utils/CommonUtils";
import passIcon from "../../common/images/pass.png";
import errorIcon from "../../common/images/error.png";
import incompleteIcon from "../../common/images/incomplete_1.png";
import preUseTestIcon from "../../common/images/pre_use_test.png";
import rightArrowIcon from "../../common/images/right_arrow.png";

const normalizePreUseStatus = (status) => {
    const value = status?.toUpperCase()?.replace(/\s+/g, "_");
    if (value === "PASSED" || value === "COMPLETE" || value === "COMPLETED") return "PASSED";
    if (value === "FAILED") return "FAILED";
    if (value === "INCOMPLETE" || value === "IN_PROGRESS") return "INCOMPLETE";
    return value || "UNKNOWN";
};

const STATUS_DISPLAY = {
    PASSED: { label: "Pass", icon: passIcon, className: "passed" },
    FAILED: { label: "Failed", icon: errorIcon, className: "failed" },
    INCOMPLETE: { label: "Incomplete", icon: incompleteIcon, className: "incomplete" },
};

function PreUseHistory(props) {
    const [selectedRowIndex, setSelectedRowIndex] = useState(0);
    const [preUseHistorydata, setPreUseHistoryData] = useState([]);
    const [endOffPage, setEndOffPage] = useState(false);
    const [createdAt, setCreatedAt] = useState(null);
    const [offset, setOffset] = useState(null);
    const isFetchingRef = useRef(false);
    const tableRef = useRef(null);
    const [isLoading, setIsLoading] = useState(false);

    const resetState = () => {
        setEndOffPage(false);
        setPreUseHistoryData([]);
        setCreatedAt(null);
        setOffset(null);
        setSelectedRowIndex(0);
    };

    const getPreUseHistory = (created, offs) => {
        if (endOffPage) return;
        setIsLoading(true);
        getVentilatorPreUseHistory({
            bedId: props.bedId,
            createdAt: created ? created?.toString() : null,
            limit: 100,
            offset: offs ? offs : null,
        })
            .then((res) => {
                if (res?.length < 100) setEndOffPage(true);
                const preUseHistoryData = res;
                if (preUseHistoryData) {
                    if (!created) setCreatedAt(preUseHistoryData?.[0]?.time);
                    setPreUseHistoryData((prevData) => {
                        setOffset(prevData.length + preUseHistoryData.length);
                        return [...prevData, ...preUseHistoryData];
                    });
                }
            })
            .finally(() => {
                isFetchingRef.current = false;
                setIsLoading(false);
            })
            .catch((err) => {
                console.log("err in getPreUseHistory: ", err);
            });
    };

    const handleScroll = useCallback(() => {
        const table = tableRef.current;
        if (table) {
            const scrollPosition = table.scrollTop + table.clientHeight;
            const isLastRows = scrollPosition >= table.scrollHeight - table.clientHeight * 0.1;
            if (isLastRows && !isFetchingRef.current && offset) {
                isFetchingRef.current = true;
                getPreUseHistory(createdAt, offset);
            }
        }
    }, [offset, createdAt, endOffPage]);

    useEffect(() => {
        const table = tableRef.current;
        if (table) {
            table.addEventListener("scroll", handleScroll);
        }

        return () => {
            if (table) {
                table.removeEventListener("scroll", handleScroll);
            }
        };
    }, [handleScroll]);

    useEffect(() => {
        if (!offset) {
            getPreUseHistory(null, null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offset]);

    useEffect(() => {
        resetState();
    }, [props.bedId]);

    const selectedRecord = preUseHistorydata?.[selectedRowIndex];
    const selectedStatus = normalizePreUseStatus(selectedRecord?.preUseStatus);
    const selectedDateTime = selectedRecord?.time ? convertUTCtoIST(selectedRecord.time) : null;

    const detailTableRows = selectedRecord?.tests?.filter((row) => {
        if (!row) return false;
        if (selectedStatus === "FAILED") return row.testStatus === "failed";
        return true;
    }) || [];

    const renderStatusPill = (status, variant = "default") => {
        const normalized = normalizePreUseStatus(status);
        const config = STATUS_DISPLAY[normalized] || {
            label: status || "Unknown",
            icon: null,
            className: "unknown",
        };

        return (
            <span className={`preuse-status-pill preuse-status-pill--${variant} ${config.className}`}>
                {config.icon ? <img alt="" src={config.icon} /> : null}
                {config.label}
            </span>
        );
    };

    if (!preUseHistorydata?.length) {
        return (
            <div className="pre-use-history-container">
                <div className="pre-use-history-page-title">Pre Use Tests</div>
                <div className="preuse-empty-state">
                    {isLoading ? (
                        <>
                            <div className="spinner"></div>
                            <div className="preuse-empty-title">Loading pre-use tests</div>
                            <div className="preuse-empty-copy">Fetching test history</div>
                        </>
                    ) : (
                        <>
                            <div className="preuse-empty-title">No pre-use test history</div>
                            <div className="preuse-empty-copy">Completed ventilator pre-use tests will appear here.</div>
                        </>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="pre-use-history-container">
            <div className="pre-use-history-page-title">Pre Use Tests</div>

            <div className="pre-use-history-tables">
                <div className="pre-use-history-tables-left">
                    <div ref={tableRef} className="preuse-list-card">
                        <table className="preuse-history-table">
                            <colgroup>
                                <col style={{ width: "10%" }} />
                                <col style={{ width: "22%" }} />
                                <col style={{ width: "22%" }} />
                                <col />
                                <col style={{ width: 40 }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    {getPreUseTimeHeader().map((header) => (
                                        <th key={header}>{header}</th>
                                    ))}
                                    <th className="preuse-history-table__chevron-col" aria-hidden="true" />
                                </tr>
                            </thead>
                            <tbody>
                                {preUseHistorydata.map((row, count) => {
                                    if (!row) return null;
                                    const isSelected = count === selectedRowIndex;
                                    const dateTime = convertUTCtoIST(row?.time);

                                    return (
                                        <tr
                                            key={count}
                                            className={isSelected ? "selected" : ""}
                                            onClick={() => setSelectedRowIndex(count)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(event) => {
                                                if (event.key === "Enter" || event.key === " ") {
                                                    setSelectedRowIndex(count);
                                                }
                                            }}
                                        >
                                            <td>{count + 1}</td>
                                            <td>{dateTime?.date || "-"}</td>
                                            <td>{dateTime?.time || "-"}</td>

                                            <td className="preuse-history-table__status-cell">
                                                {renderStatusPill(row?.preUseStatus)}
                                            </td>

                                            <td className="preuse-history-table__chevron">
                                                <img src={rightArrowIcon} alt="" aria-hidden="true" />
                                            </td>

                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        {isLoading && (
                            <div className="spinner-container">
                                <div className="spinner"></div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="pre-use-history-tables-right">
                    <div className="preuse-detail-card">
                        <div className="preuse-detail-header">
                            <div className="preuse-detail-header-left">
                                <img
                                    className="preuse-detail-icon"
                                    src={preUseTestIcon}
                                    alt=""
                                    aria-hidden="true"
                                />
                                <div>
                                    <div className="preuse-detail-title">
                                        Test ID : {selectedRowIndex + 1}
                                    </div>
                                    <div className="preuse-detail-time">
                                        {selectedDateTime?.date || "-"} • {selectedDateTime?.time || "-"}
                                    </div>
                                </div>
                            </div>
                            {renderStatusPill(selectedRecord?.preUseStatus, "large")}
                        </div>

                        <div className="preuse-detail-section-heading">
                            <div className="preuse-detail-section-title">Test Details</div>
                        </div>

                        {selectedStatus === "PASSED" ? (
                            <div className="preuse-all-passed">
                                <img src={passIcon} alt="" aria-hidden="true" />
                                <div className="preuse-all-passed__text">All Test Case Passed</div>
                            </div>
                        ) : !selectedRecord?.tests?.length ? (
                            <div className="preuse-detail-empty">No test details available for this record.</div>
                        ) : (
                            <div className="preuse-detail-table-wrap">
                                <div className="alarm-table-container">
                                    <table className="alarm-table">
                                        <thead className="sticky">
                                            <tr>
                                                {getPreUseModuleHeader().map((data, count) => (
                                                    <th key={count} className="alarm-header">
                                                        <div className="header-content">{data}</div>
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {detailTableRows.map((row, count) => (
                                                <tr key={count}>
                                                    <td>{row.moduleName || "-"}</td>
                                                    <td>{row.testName || "-"}</td>
                                                    <td>
                                                        {(row.testId && row.errorCode)
                                                            ? ((row.testId - 113000).toString().padStart(2, "0") + row.errorCode)
                                                            : "--"}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    {detailTableRows.length === 0 && (
                                        <div className="preuse-detail-empty compact">No matching test rows.</div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

const mapStateToProps = () => ({});

export default connect(mapStateToProps, null)(PreUseHistory);
